import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { COOKIES } from '@avanta/shared/constants/storage';
import { t } from '@avanta/shared/i18n';
import {
  oauthToken,
  loginWithPassword,
  refreshAccessToken,
  logout as logoutShared,
  recoverPassword,
  verifyRecoveryCode,
  resendRecoveryCode,
  changePassword,
  changeUserPassword,
  loginWithMagicLink,
} from '@avanta/shared/services/auth';
import { clearRecoveryCookies } from '@avanta/shared/utils/auth';
import { setCookieWithoutExpiry } from '@avanta/shared/utils/storage';
import { queryClient } from '@/query-manager';
import { ROUTES } from 'constants/routes';
import { useUserStore } from 'stores/user';
import { AcceptedUsers, type AuthInfo } from 'types/auth';

const login = async (
  username: string,
  password: string,
  magicToken?: string
): Promise<AuthInfo> => {
  const token = await oauthToken(username, password, magicToken);
  if (!token) {
    throw new Error(t('Login.error', { ns: LOCIZE_NS.AUTH }));
  }
  const expiresIn = token.data.expires_in;
  setCookieWithoutExpiry(COOKIES.REFRESH_TOKEN, token.data.refresh_token);

  const user = magicToken
    ? await loginWithMagicLink(token.data.access_token, username)
    : await loginWithPassword(username, password);

  const isAcceptedType = AcceptedUsers.includes(user.data.type as (typeof AcceptedUsers)[number]);

  if (!isAcceptedType) {
    await logout(false);
    throw new Error(t('Login.error', { ns: LOCIZE_NS.AUTH }));
  }

  useUserStore.getState().setUser(user.data);
  setCookieWithoutExpiry(COOKIES.USER_ID, user.data.id);
  clearRecoveryCookies();

  if (user.data.requiredProfileSetup) {
    window.location.href = ROUTES.PROFILE_SETUP;
    return token.data;
  }

  return {
    user: user.data,
    access_token: token.data.access_token,
    expires_in: expiresIn,
    refresh_token: token.data.refresh_token,
    scope: token.data.scope,
    token_type: token.data.token_type,
  };
};

const logout = async (redirect = true): Promise<void> => {
  queryClient.clear();
  logoutShared(redirect);
};

export type AuthServiceTypes = {
  login: (username: string, password: string, magicToken?: string) => Promise<AuthInfo>;
  refreshAccessToken: (refreshToken: string) => Promise<string | null>;
  logout: (redirect?: boolean) => Promise<void>;
  recoverPassword: (email: string) => Promise<void>;
  verifyRecoveryCode: (params: { validation_code: string; email: string }) => Promise<void>;
  changePassword: (params: {
    email: string;
    password: string;
    validation_code: string;
  }) => Promise<void>;
  resendRecoveryCode: (email: string) => Promise<void>;
  changeUserPassword: (params: { currentPassword: string; newPassword: string }) => Promise<void>;
};

export const AuthService: AuthServiceTypes = {
  login: async (username: string, password: string, magicToken?: string) =>
    login(username, password, magicToken),
  refreshAccessToken: async (refreshToken: string) => refreshAccessToken(refreshToken),
  logout,
  recoverPassword,
  verifyRecoveryCode,
  changePassword,
  resendRecoveryCode,
  changeUserPassword,
};
